import _ from 'lodash';
import moment from 'moment';

import { getPrettyPriceDisplay, pluralizeCount } from './formatting';
import { formatNumber, readable } from './url_data_agent_evaluations_helpers';
import { formatPrice } from './url_data_agent_live_matches_helpers';
import { getOpportunityHoldReasonCopy } from './url_data_agent_opportunities_helpers';

/* ────────────────────────────────────────────────────────────────
 * Starting a batch
 * ─────────────────────────────────────────────────────────────── */

// The park route's limits: pins per request, and the SOP's length.
const PARK_CHUNK_SIZE = 1000;
export const MAX_SOP_CHARS = 2500;
// create:<name>:<YYYYMMDDHHmm> fits the intake tag's 64 characters with a name of up to 44.
const MAX_BATCH_NAME_CHARS = 44;
const UNPARSED_TOKENS_SHOWN = 5;

/**
 * The pin ids in pasted text or an uploaded .csv/.txt: the Pin ID column when the first line names one, otherwise every integer.
 * Each id keeps its first position; repeats are counted and dropped.
 * @returns {{ pinIds: number[], duplicates: number, unparsed: string[], fromColumn: boolean }}
 */
export const parsePinIds = text => {
  const lines = String(text || '')
    .split(/\r?\n/)
    .filter(line => line.trim());
  const column = lines.length ? splitCsvLine(lines[0]).findIndex(isPinIdHeader) : -1;
  const tokens = column === -1 ? lines.flatMap(line => line.split(/[\s,;]+/)) : lines.slice(1).map(line => splitCsvLine(line)[column] || '');
  const [valid, invalid] = _.partition(tokens.map(token => _.trim(token, ' "')).filter(Boolean), isPinIdToken);
  const pinIds = _.uniq(valid.map(Number));
  return { pinIds, duplicates: valid.length - pinIds.length, unparsed: _.uniq(invalid), fromColumn: column !== -1 };
};

export const chunkPinIds = pinIds => _.chunk(pinIds, PARK_CHUNK_SIZE);

// The line under the pin box, and the first tokens that were left out.
export const getParsedPinsNotes = ({ pinIds, duplicates, unparsed, fromColumn }) => {
  const notShown = unparsed.length - UNPARSED_TOKENS_SHOWN;
  return {
    count: _.compact([
      `${formatNumber(pinIds.length)} ${pinIds.length === 1 ? 'pin ID' : 'pin IDs'}${fromColumn ? ' from the Pin ID column' : ''}`,
      duplicates > 0 && `${formatNumber(duplicates)} ${duplicates === 1 ? 'repeat' : 'repeats'} removed`
    ]).join(' · '),
    unparsed: unparsed.length
      ? `Left out, not pin IDs: ${unparsed.slice(0, UNPARSED_TOKENS_SHOWN).join(', ')}${notShown > 0 ? ` and ${formatNumber(notShown)} more` : ''}`
      : null
  };
};

// The batch name as its tag spells it: lowercase letters, digits and _ . -, starting with a letter or digit.
export const toCreationBatchName = name => {
  const slug = _.trimStart(_.deburr(name).toLowerCase().replace(/[^a-z0-9_.-]+/g, '-'), '_.-');
  return _.trimEnd(slug.slice(0, MAX_BATCH_NAME_CHARS), '-');
};

/**
 * The batch's tag, create:<name>:<the UTC minute it started>, or null when the name keeps no letter or digit.
 */
export const toCreationBatchTag = (name, now = new Date()) => {
  const batchName = toCreationBatchName(name);
  return batchName ? `create:${batchName}:${moment.utc(now).format('YYYYMMDDHHmm')}` : null;
};

const PARK_COUNT_KEYS = ['onIgnoredDomains', 'awaitingCrawl', 'inLinksToCheckWhileWaiting', 'bulkDismissed', 'replacedCreationRequests'];

export const EMPTY_PARK_REPORT = { links: 0, ..._.fromPairs(PARK_COUNT_KEYS.map(key => [key, 0])), skipped: {} };

// A batch's park report after one more chunk's answer; a check's wouldPark counts as its links. Pins in different chunks that
// share a link are counted in each.
export const addParkResult = (report, result) => ({
  links: report.links + (result.parked ?? result.wouldPark),
  ..._.fromPairs(PARK_COUNT_KEYS.map(key => [key, report[key] + result[key]])),
  skipped: _.mergeWith({}, report.skipped, result.skipped, (pinIds, more) => (pinIds || []).concat(more))
});

/**
 * What reading `links` more links costs, and how long APM takes at least to reach them at its spare pace. APM's own traffic reads
 * first, so the time is a floor.
 * @param {{ queuedLinks: number, sparePerHour: number, newProductRunCostUsd: number|null }} lane — the batch list's lane
 */
export const getParkEstimate = (links, lane) => {
  const cost = lane?.newProductRunCostUsd == null ? null : links * lane.newProductRunCostUsd;
  const hours = lane?.sparePerHour > 0 ? (lane.queuedLinks + links) / lane.sparePerHour : null;
  return {
    cost: cost == null ? 'unknown until APM has recent new-product runs to price' : `about ${getPrettyPriceDisplay(cost, { precision: cost < 10 ? 2 : 0 })}`,
    time:
      hours == null
        ? 'unknown while APM has no spare room this hour'
        : `${hours < 1 ? 'under an hour' : `at least about ${formatHours(hours)}`} of reading, longer while APM’s own traffic is busy`
  };
};

/* ────────────────────────────────────────────────────────────────
 * Where each link of a batch stands
 * ─────────────────────────────────────────────────────────────── */

// Mirror of LINK_STATES (node/utils/helpers/url_data/silent_eval_creation_requests.js), in the order a link moves through them.
export const CREATION_LINK_STATES = [
  { value: 'waiting', label: 'Waiting' },
  { value: 'being_read', label: 'Being read' },
  { value: 'ready', label: 'Ready' },
  { value: 'creating', label: 'Creating' },
  { value: 'created', label: 'Created' },
  { value: 'needs_person', label: 'Needs a person' },
  { value: 'done_elsewhere', label: 'Done elsewhere' }
];

// The batch view's selector leads with Ready, where a batch waits on a person.
export const CREATION_LINK_STATE_OPTIONS = [
  _.find(CREATION_LINK_STATES, { value: 'ready' }),
  ...CREATION_LINK_STATES.filter(state => state.value !== 'ready'),
  { value: 'all', label: 'All' }
];

// The server's count keys for the link states (`links.beingRead`, `reasons.needsPerson`, …).
export const getCreationCountKey = state => _.camelCase(state);

// Reasons only creation batches give (linkReason in silent_eval_creation_requests.js); the apply-skip and opportunity hold copy explains the rest.
const CREATION_REASON_COPY = {
  not_found: 'The pin has no link, or its link has no page record',
  not_crawlable: 'The page is dead, or unread with no crawl scheduled',
  already_matched: 'Already matched to a catalog product',
  dismissed: 'Dismissed by a person or an earlier APM run',
  already_queued: 'Already waiting for APM in another park',
  active_evaluation: 'APM was already reading this link',
  already_requested: 'Approved for creation in another batch',
  already_evaluated: 'An applied, undone or reviewed APM run keeps this link',
  awaiting_crawl: 'Waiting for the crawler, or for APM to read the page two days after the pin',
  undone: 'The creation was undone',
  run_missing: 'APM’s run for this link is gone',
  reviewed_evaluation: 'A reviewed APM run keeps this link',
  urldata_missing: 'The link’s page record is gone',
  prior_applied_evaluation: 'An earlier APM run already matched this link',
  creation_request_evaluation: 'Another batch holds this link',
  resolved_by_person: 'A person matched or dismissed it in Links to Check',
  apply_error: 'Creating the product failed three times'
};

export const getCreationReasonCopy = reason => (reason ? CREATION_REASON_COPY[reason] || getOpportunityHoldReasonCopy(reason) : null);

export const getCreationLinkStateLabel = link => {
  if (link.state === 'created' && link.evaluation?.linkedToExisting) {
    const product = link.evaluation.createdProduct;
    return `Linked to existing${product ? ` #${product.id}` : ''}`;
  }
  return _.find(CREATION_LINK_STATES, { value: link.state })?.label || readable(link.state);
};

/**
 * One line on why the link is in its state, or what happens to it next.
 * @param {{ mode: string, unreadable: boolean }} lane — APM's switch, from the latest read
 */
export const getCreationLinkNote = (link, lane) => {
  const { state, reason, evaluation, product, matchedBy } = link;
  const isLaneStopped = Boolean(getLaneStopNote(lane));
  const approvedBy = evaluation?.approvedBy ? `Approved by ${evaluation.approvedBy.name}` : null;
  if (state === 'waiting' && reason === 'awaiting_crawl') return 'Waiting for the crawler, or for APM two days after the pin';
  if (state === 'waiting') return isLaneStopped ? 'Waiting for APM to resume' : 'Waiting behind APM’s own links';
  if (state === 'being_read') return 'APM is reading the page';
  if (state === 'ready') return evaluation?.policyRefusal ? `APM would not create it now: ${_.lowerFirst(getCreationReasonCopy(evaluation.policyRefusal))}` : null;
  if (state === 'creating') return _.compact([approvedBy, isLaneStopped && 'waiting for APM to resume']).join(' · ') || null;
  if (state === 'created') return approvedBy;
  if (state === 'done_elsewhere' && product) return `Matched to #${product.id}${matchedBy ? ` by ${matchedBy.name}` : ''}`;
  if (state === 'done_elsewhere' && link.dismissedAt) return `Dismissed ${moment(link.dismissedAt).format('MMM D')}`;
  if (reason === 'undone' && evaluation?.revertedBy) return `Undone by ${evaluation.revertedBy.name}`;
  if (state === 'needs_person' && evaluation?.archived) return `${getCreationReasonCopy(reason)}. Its run was archived, so read it again in a new batch`;
  if (reason === 'apply_error' && evaluation?.lastCreationError) return `${getCreationReasonCopy(reason)}: ${evaluation.lastCreationError}`;
  return getCreationReasonCopy(reason);
};

const VERDICT_COPY = {
  new_product: 'APM found no catalog product for it',
  sibling: 'APM matched it to a catalog product',
  new_variant: 'APM matched it to a catalog product',
  dismiss: 'APM judged it not a product page',
  ambiguous: 'APM could not choose between close catalog products'
};

// APM's own answer, which the catalog team's request overrules.
export const getCreationLinkVerdict = evaluation => {
  const verdict = evaluation?.matchVerdict;
  return verdict ? VERDICT_COPY[verdict] || `APM answered “${readable(verdict)}”` : null;
};

const getProductBrandLabel = productBrand =>
  productBrand.isNew ? `New brand: ${productBrand.name}` : `${productBrand.name || 'Catalog brand'} #${productBrand.AllBrand_id}`;

/**
 * The product a row shows: what APM will create while the link is ready or creating, the catalog product once created,
 * and otherwise what the crawler read from the page.
 * @returns {{ title: string, image: string|null, brand: string|null, categoryName: string|null, price: string|null, Product_id: number|null }}
 */
export const getCreationLinkProduct = link => {
  const { page, evaluation } = link;
  const toCreate = evaluation?.productToCreate;
  if (toCreate) {
    return {
      title: toCreate.title,
      image: toCreate.image,
      brand: getProductBrandLabel(evaluation.productBrand),
      categoryName: evaluation.categoryName,
      price: formatPrice(toCreate.fallbackPrice, toCreate.fallbackPriceCurrency),
      Product_id: null
    };
  }
  const created = link.state === 'created' ? evaluation?.createdProduct : null;
  if (created) {
    return {
      title: created.title,
      image: created.image,
      brand: created.brand,
      categoryName: created.category?.name || null,
      price: formatPrice(created.fallbackPrice, created.fallbackPriceCurrency),
      Product_id: created.id
    };
  }
  return {
    title: page.title || evaluation?.agentProductName || link.urlStem || link.sampleUrl || 'Unknown page',
    image: page.image || evaluation?.agentPrimaryImageUrl || null,
    brand: page.brand || evaluation?.agentBrandName || null,
    categoryName: evaluation?.categoryName || null,
    price: null,
    Product_id: null
  };
};

/* ────────────────────────────────────────────────────────────────
 * What a person can do with a link
 * ─────────────────────────────────────────────────────────────── */

// Refusals a catalog brand answers; the identity refusal can take a title instead.
const BRAND_FIX_REASONS = ['creation_identity_incomplete', 'brand_possible_duplicate', 'creation_brand_conflict'];
const BRAND_REQUIRED_REASONS = ['brand_possible_duplicate', 'creation_brand_conflict'];

export const isCreationLinkApprovable = link => link.state === 'ready' && Boolean(link.evaluation?.productToCreate);

/**
 * The fix a refused link takes, or null: a catalog brand (and for an identity refusal, a title), or another try after an error.
 * Either reopens the link approved by the person, once the policy passes. An archived run can only be read again in a new batch.
 */
export const getCreationLinkFix = link => {
  const { state, reason, evaluation } = link;
  if (state !== 'needs_person' || !evaluation || evaluation.archived) return null;
  if (reason === 'apply_error') return { kind: 'retry' };
  if (!BRAND_FIX_REASONS.includes(reason)) return null;
  return {
    kind: 'brand',
    requiresBrand: BRAND_REQUIRED_REASONS.includes(reason),
    takesTitle: reason === 'creation_identity_incomplete',
    suggestedBrand: evaluation.suggestedBrand
  };
};

// Every button on the row in display order; the one marked primary is the dark button.
export const getCreationLinkActions = link => {
  if (link.state === 'ready') {
    return [
      ...(isCreationLinkApprovable(link) ? [{ key: 'approve', display: 'Approve', primary: true }] : []),
      { key: 'edit_title', display: 'Edit title' },
      { key: 'change_brand', display: 'Change brand' }
    ];
  }
  if (link.state === 'created') return [{ key: 'undo', display: 'Undo' }];
  const fix = getCreationLinkFix(link);
  if (fix?.kind === 'retry') return [{ key: 'retry', display: 'Try again', primary: true }];
  if (fix?.kind === 'brand') return [{ key: 'fix', display: 'Choose brand…', primary: true }];
  return [];
};

// The page after one link was re-read: the row swapped in place, and the link counted under its new state.
export const replaceCreationLink = (report, link) => {
  const previous = report.rows.find(row => row.hashedUrlStem === link.hashedUrlStem);
  if (!previous) return report;
  const from = getCreationCountKey(previous.state);
  const to = getCreationCountKey(link.state);
  const links = from === to ? report.links : { ...report.links, [from]: report.links[from] - 1, [to]: report.links[to] + 1 };
  return { ...report, links, rows: report.rows.map(row => (row === previous ? link : row)) };
};

export const getApprovalMessage = ({ approved, noLongerReady }) => {
  const approval = `Approved ${formatNumber(approved)} ${approved === 1 ? 'product' : 'products'} for APM to create`;
  return noLongerReady ? `${approval}; ${formatNumber(noLongerReady)} no longer ready.` : `${approval}.`;
};

// A refused fix says what the policy still refuses.
export const getCreationRequestErrorMessage = error => {
  const message = error?.message || String(error || 'Something went wrong');
  const policyReason = error?.extra?.policyReason;
  return policyReason ? `${_.trimEnd(message, '.')}: ${_.lowerFirst(getCreationReasonCopy(policyReason))}.` : message;
};

/* ────────────────────────────────────────────────────────────────
 * Batches
 * ─────────────────────────────────────────────────────────────── */

export const getBatchModeLabel = batch => (batch.createWhenReady ? 'Create when ready' : 'Review first');

// Why nothing in a batch moves: APM is paused, or its switch could not be read.
export const getLaneStopNote = lane => {
  if (lane?.unreadable) return 'APM’s switch could not be read, so nothing is created until it can.';
  if (lane?.mode === 'paused') return 'APM is paused: nothing is read or created until it resumes.';
  return null;
};

// How long the batch's unread links take at the pace of its last hour.
export const getBatchTimeLeft = ({ links, pace }) => {
  const unread = links.waiting + links.beingRead;
  if (!unread) return null;
  if (!pace.readLastHour) return 'time left unknown until APM reads one of its links';
  const hours = unread / pace.readLastHour;
  return hours < 1 ? 'under an hour left' : `about ${formatHours(hours)} left`;
};

const formatHours = hours => pluralizeCount(_.round(hours, hours < 10 ? 1 : 0), 'hour');

// A CSV line's cells, split on commas, tabs or semicolons outside double quotes.
const splitCsvLine = line => {
  const cells = [''];
  let isQuoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"' && isQuoted && line[index + 1] === '"') {
      cells[cells.length - 1] += '"';
      index += 1;
    } else if (char === '"') {
      isQuoted = !isQuoted;
    } else if (!isQuoted && /[,\t;]/.test(char)) {
      cells.push('');
    } else {
      cells[cells.length - 1] += char;
    }
  }
  return cells.map(cell => cell.trim());
};

const isPinIdHeader = cell => /^pin(id)?s?$/.test(cell.toLowerCase().replace(/[^a-z]/g, ''));

const isPinIdToken = token => /^\d+$/.test(token) && Number.isSafeInteger(Number(token)) && Number(token) > 0;
